// What the Preact chrome reaches (set once by ui/mount.tsx): the shell controller, the actions main.ts wires
// to the game / router / audio / save, the DOM icon provider and the current game data. Components read the
// signals of ui/state.ts for WHAT to show and call these for what to DO — no component dispatches around
// the player's dispatch or reads the sim outside sim/query.ts.
import type { DomIcons } from '../art/domIcons.ts';
import type { GameData } from '../data/gameData.ts';
import type { Tool } from '../input/modes.ts';
import type { Layout } from '../layout/computeLayout.ts';
import type { CmdResult, Command, RejectReason } from '../sim/commands.ts';
import type { ShopId } from '../sim/gen/ids.ts';
import type { SimState } from '../sim/state.ts';
import type { Settings } from './settings.ts';
import type { Shell } from './shell.ts';

export interface ShellActions {
  /** The player's dispatch (the router's: events fan out in this task, M9 noteAction included). */
  dispatch(cmd: Command): CmdResult;
  /** A refusal of a UI button: the red reason bubble above `at` (client px) + the rate-limited bonk. */
  refuse(reason: RejectReason, at: { x: number; y: number }, bid?: number | null): void;
  /** PLAY ⇄ BUILD (the Build tab / Done), a Build tool, the Build bar's Undo. */
  toggleBuild(): void;
  setTool(tool: Tool): void;
  undoBuild(): void;
  /** A tap on the counter / Sell tool while something is selected (M6 `router.tapZone`). */
  tapZone(zone: 'counter' | 'sell'): void;
  /** True while a tap-select / tap-placement waits for its target. */
  selected(): boolean;
  /** §2.8 step 7: the pop-over's Place button (valid cells glow, a board tap buys). */
  beginTapPlace(item: ShopId): void;
  /** Settings changed (applied to audio / quality / motion and saved in meta.settings). */
  settings(next: Settings): void;
  /** Settings → Export telemetry (share → download → copy textarea); resolves to the method used. */
  exportTelemetry(): Promise<string>;
  /** Settings → Reset save (after the 2 s hold): a fresh cold open. */
  resetSave(): void;
  /** A UI click sound (buttons). */
  click(): void;
}

export interface ShellCtx {
  shell: Shell;
  actions: ShellActions;
  icons: DomIcons;
  data(): GameData;
  state(): SimState;
  layout(): Layout | null;
  /** ?test (fx.instant): no tweens, no idle animation (deterministic screenshots). */
  instant: boolean;
  version: string;
  buildId: string;
}

let current: ShellCtx | null = null;

export function setShellCtx(c: ShellCtx | null): void {
  current = c;
}

export function shellCtx(): ShellCtx {
  if (!current) throw new Error('UI shell not mounted');
  return current;
}
